import _ from 'lodash';
import {createSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {ActivityVisualTypesEnum} from '../../models/api/activityModel';
import {getActivities, getConversations, getMessages} from '../../store/data/dataSelectors';
import {getUserId} from '../../store/data/teammates/teammatesSelectors';
import {getFlatPanels} from '../../store/panels/panelsSelectors';
import {PanelTypesEnum} from '../../store/panels/panelStateBase';
import {ViewerPanelPageTypesEnum} from '../../store/panels/viewer/viewerPanelState';

/** Get the first cells we can find in the state. */
const getCells = createSelector(getFlatPanels, (panels) => {
  const panel = panels.find((p) => p.type === PanelTypesEnum.CELLS);
  if (!panel || panel.type !== PanelTypesEnum.CELLS) {
    return undefined;
  }

  return panel;
});

/** Get the currently active cells parameters. */
export const getCellsParameters = createSelector(getCells, (cells) => {
  const page = cells && cells.pages.current;
  return page && page.parameters;
});

/** Get the first viewer we can find in the state. */
const getViewer = createSelector(getFlatPanels, (panels) => {
  const panel = panels.find((p) => p.type === PanelTypesEnum.VIEWER);
  if (!panel || panel.type !== PanelTypesEnum.VIEWER) {
    return undefined;
  }

  return panel;
});

/** Get the currently active page in the first viewer panel. */
export const getViewerPage = createSelector(getViewer, (viewerPanel) => {
  if (!viewerPanel) {
    return undefined;
  }

  return viewerPanel.pages.current || viewerPanel.pages.next;
});

export const getViewerTeammateId = createSelector(
  [getUserId, getViewerPage],
  (userId, viewerPage) => (viewerPage && viewerPage.cellsContext.teammateId) || userId,
);

/** Get the conversation currently display in the active viewer page. */
export const getViewerConversation = createSelector(
  [getViewerPage, getConversations],
  (viewerPage, conversations) => {
    if (
      viewerPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
      viewerPage.status !== AsyncStatusesEnum.LOADED
    ) {
      return undefined;
    }

    return conversations[viewerPage.conversationId];
  },
);

/** Get the activities for the currently selected conversation. */
const getViewerActivities = createSelector([getViewerPage, getActivities], (viewerPage, allActivities) => {
  if (
    viewerPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
    viewerPage.status !== AsyncStatusesEnum.LOADED
  ) {
    return undefined;
  }

  const {activities} = viewerPage;
  return _(activities)
    .keys()
    .map((id) => allActivities[Number(id)])
    .compact()
    .value();
});

/** Get the messages for the currently selected conversation. */
export const getViewerMessages = createSelector(
  [getViewerActivities, getMessages],
  (activities, allMessages) =>
    _(activities)
      .sortBy((a) => a.date)
      .map((a) => {
        if (a.visualType !== ActivityVisualTypesEnum.MESSAGE) {
          return null;
        }

        return allMessages[a.messageUid];
      })
      .compact()
      .value(),
);
